/**
 * Qué patios tiene una tarjeta de acceso, y si pasa el filtro por patio de la
 * pantalla de tarjetas.
 *
 * Módulo puro a propósito: la página importa el store, que importa `utils/api`
 * → firebase, y en un worktree sin `apps/v5/.env` eso revienta al colectar el
 * test. `import type` se borra al compilar, así que no arrastra nada.
 */
import type { AccessCard } from "../../../store/oasis/hikvisionAccessStore";
import {
  NO_YARD_FILTER,
  matchesYardSelection,
} from "../components/yardMultiFilter";

type CardYardFields = Pick<AccessCard, "yard_ids" | "yard_id">;

/**
 * Valor del filtro para "tarjetas sin ningún patio". Vive en el módulo del
 * filtro múltiple, compartido con Vehículos, Grupos y Personal; se re-exporta
 * acá para no mover los imports existentes.
 */
export { NO_YARD_FILTER };

/**
 * Patios de la tarjeta. `yard_ids` manda cuando existe, **aunque sea `[]`**, y
 * el legacy `yard_id` sólo se usa si `yard_ids` no está.
 *
 * Es la misma regla (`??`) con la que `openEdit` carga el formulario, y tiene
 * que serlo: la lista tiene que mostrar lo que el formulario va a guardar. El
 * caso donde divergen existe en datos reales, no es teórico —
 * `truckYardController.js` (al asignar un tag desde el vehículo) y el import de
 * `/cards/result` en `functions/index.js` escriben sólo `yard_id`, sin tocar
 * `yard_ids`. Una tarjeta con `yard_ids: []` que después pasa por ahí queda con
 * `yard_id` seteado. Si acá ganara el legacy, la card diría "Patio X", el
 * formulario abriría vacío y al guardar mandaría `yard_id: null`, borrándolo.
 * Mostrar "Sin patio" es lo que efectivamente hay en el formulario.
 *
 * La otra cara de la misma regla, y hay que decirla: si `yard_ids` ya existe
 * con patios, esos writers lo dejan tal cual mientras cambian `yard_id`, así
 * que la card puede mostrar un patio desactualizado (`yard_ids: ["A"]` al lado
 * de un `yard_id: "C"` recién escrito, o un patio borrado, porque el hard-delete
 * de patios limpia `yard_id` y no `yard_ids`). No se corrige eligiendo el
 * legacy acá: el formulario carga con esta misma función y guarda `yard_ids`,
 * así que la card volvería a mostrar algo que la edición pisa. Se corrige
 * cuando esos writers escriban `yard_ids`.
 */
export function resolveCardYardIds(card: CardYardFields): string[] {
  return card.yard_ids ?? (card.yard_id ? [card.yard_id] : []);
}

/**
 * Si la tarjeta pasa el filtro de patios. Acepta la selección múltiple
 * (`[]` = todos) y, por compatibilidad, un solo valor (`""` = todos).
 * `NO_YARD_FILTER` = tarjetas con cero patios. Una tarjeta multi-patio pasa si
 * CUALQUIERA de sus patios está elegido. La regla es la de
 * `matchesYardSelection`; acá sólo se resuelve qué patios tiene la tarjeta.
 */
export function matchesYardFilter(
  card: CardYardFields,
  filter: string | readonly string[],
): boolean {
  const selected = typeof filter === "string" ? (filter ? [filter] : []) : filter;
  return matchesYardSelection(resolveCardYardIds(card), selected);
}

/**
 * Nombres para mostrar. Un id que no está en el mapa se muestra tal cual, como
 * hacen las pills del formulario: puede ser un patio borrado (el hard-delete de
 * patios limpia `yard_id` pero no `yard_ids`) o uno fuera del alcance del
 * usuario, y ocultarlo haría parecer que la tarjeta tiene menos patios.
 */
export function cardYardNames(
  card: CardYardFields,
  yardNameById: Record<string, string>,
): string[] {
  return resolveCardYardIds(card).map((id) => yardNameById[id] || id);
}
